import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { AdCard } from "@/components/ad-card";
import { CategoryIcon } from "@/components/category-icon";
import { SEO_DEFAULTS, fetchSeoSettings, renderSeo } from "@/lib/seo-settings";
import { categories, categoryName, subcategoriesOf } from "@/lib/data";
import { brandFilterFor, matchesBrand } from "@/lib/specs";
import { useAllAds } from "@/routes/elanlar";

export const Route = createFileRoute("/kateqoriya/$slug")({
  validateSearch: (search: Record<string, unknown>) => ({
    alt: typeof search['alt'] === "string" ? (search['alt'] as string) : "",
    marka: typeof search['marka'] === "string" ? (search['marka'] as string) : "",
  }),
  loaderDeps: ({ search }) => ({ alt: search.alt }),
  loader: async ({ params, deps }) => {
    const cat = categories.find((c) => c.slug === params.slug);
    if (!cat) throw notFound();
    const sub = subcategoriesOf(cat.slug).find((s) => s.slug === deps.alt) ?? null;
    return { cat, sub, seo: await fetchSeoSettings() };
  },
  head: ({ params, loaderData, match }) => {
    const name = categoryName(params.slug);
    const seo = loaderData?.seo ?? SEO_DEFAULTS;
    const sub = loaderData?.sub ?? null;
    const label = sub ? `${sub.name} — ${name}` : name;
    const vars = { kateqoriya: label };
    const title = renderSeo(seo["category_title_template"] || SEO_DEFAULTS["category_title_template"]!, vars);
    const description = sub
      ? `${sub.name} (${name}) elanları — Albazar.az saytında ən yeni ${sub.name.toLowerCase()} elanları, real qiymətlər və birbaşa satıcı ilə əlaqə.`
      : renderSeo(
          seo["category_description_template"] || SEO_DEFAULTS["category_description_template"]!,
          vars,
        );
    const keywords = [
      sub ? [sub.name, ...sub.keywords].join(", ") : "",
      renderSeo(seo["category_keywords_template"] || "", vars),
      seo["global_keywords"] ?? "",
    ]
      .filter(Boolean)
      .join(", ");
    const url = `https://albazar.az/kateqoriya/${params.slug}${sub ? `?alt=${sub.slug}` : ""}`;
    const subs = subcategoriesOf(params.slug);
    return {
      meta: [
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
        { title },
        { name: "description", content: description },
        { name: "keywords", content: keywords },
        { name: "robots", content: match.search?.marka ? "noindex, follow" : "index, follow, max-image-preview:large" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:site_name", content: "Albazar.az" },
        { property: "og:locale", content: "az_AZ" },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "BreadcrumbList",
                itemListElement: [
                  { "@type": "ListItem", position: 1, name: "Ana səhifə", item: "https://albazar.az/" },
                  {
                    "@type": "ListItem",
                    position: 2,
                    name,
                    item: `https://albazar.az/kateqoriya/${params.slug}`,
                  },
                  ...(sub
                    ? [{ "@type": "ListItem", position: 3, name: sub.name, item: url }]
                    : []),
                ],
              },
              {
                "@type": "CollectionPage",
                name: title,
                description,
                url,
                inLanguage: "az-AZ",
                isPartOf: { "@type": "WebSite", name: "Albazar.az", url: "https://albazar.az" },
                ...(sub || subs.length === 0
                  ? {}
                  : {
                      hasPart: subs.slice(0, 50).map((s) => ({
                        "@type": "CollectionPage",
                        name: s.name,
                        url: `https://albazar.az/kateqoriya/${params.slug}?alt=${s.slug}`,
                      })),
                    }),
              },
            ],
          }),
        },
      ],
    };
  },
  component: CategoryPage,
});

function CategoryPage() {
  const { cat } = Route.useLoaderData();
  const allAds = useAllAds();
  const { alt, marka } = Route.useSearch();
  const sub = alt || null;
  const subs = subcategoriesOf(cat.slug);
  const brandFilter = brandFilterFor(cat.slug);
  const inCat = allAds
    .filter((a) => a.category === cat.slug && a.status === "active")
    .filter((a) => (marka ? matchesBrand(a, marka) : true));
  const active = subs.find((s) => s.slug === sub) ?? null;
  const list = active
    ? inCat.filter((a) => {
        const hay = `${a.title} ${a.description ?? ""}`.toLowerCase();
        return active.keywords.some((k) => hay.includes(k.toLowerCase()));
      })
    : inCat;

  return (
    <div className="container-x py-8">
      <nav className="text-sm text-muted-foreground">
        <Link to="/" className="hover:text-primary">
          Ana səhifə
        </Link>{" "}
        / <span className="text-foreground">{cat.name}</span>
      </nav>
      <h1 className="mt-3 flex items-center gap-3 text-3xl font-extrabold tracking-tight">
        <span className="grid size-12 place-items-center rounded-2xl bg-accent text-2xl">
          <CategoryIcon slug={cat.slug} name={cat.name} className="size-10" />
        </span>
        {cat.name}
      </h1>
      <p className="mt-1 text-sm text-muted-foreground">{list.length} aktiv elan</p>

      {subs.length > 0 && (
        <div className="mt-5 rounded-3xl border border-border bg-card p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Alt kateqoriyalar
          </p>
          <div className="mt-3 -mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
            <Link
              to="/kateqoriya/$slug"
              params={{ slug: cat.slug }}
              search={{ alt: "", marka }}
              className={`shrink-0 rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                sub === null
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border hover:border-primary/50 hover:bg-accent"
              }`}
            >
              Hamısı
            </Link>
            {subs.map((s) => (
              <Link
                key={s.slug}
                to="/kateqoriya/$slug"
                params={{ slug: cat.slug }}
                search={{ alt: s.slug, marka }}
                className={`shrink-0 rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                  sub === s.slug
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border hover:border-primary/50 hover:bg-accent"
                }`}
              >
                {s.name}
              </Link>
            ))}
          </div>
        </div>
      )}

      {brandFilter && (
        <div className="mt-4 rounded-3xl border border-border bg-card p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Marka
          </p>
          <div className="mt-3 -mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
            <Link
              to="/kateqoriya/$slug"
              params={{ slug: cat.slug }}
              search={{ alt, marka: "" }}
              className={`shrink-0 rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${
                !marka
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border hover:border-primary/50 hover:bg-accent"
              }`}
            >
              Hamısı
            </Link>
            {brandFilter.brands.map((b) => (
              <Link
                key={b}
                to="/kateqoriya/$slug"
                params={{ slug: cat.slug }}
                search={{ alt, marka: b }}
                className={`shrink-0 rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${
                  marka === b
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border hover:border-primary/50 hover:bg-accent"
                }`}
              >
                {b}
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 flex flex-wrap gap-2">
        {categories.map((c) => (
          <Link
            key={c.slug}
            to="/kateqoriya/$slug"
            params={{ slug: c.slug }}
            search={{ alt: "", marka: "" }}
            className={`rounded-full border px-3.5 py-1.5 text-sm transition-colors ${
              c.slug === cat.slug
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border hover:border-primary/50 hover:bg-accent"
            }`}
          >
            {c.icon} {c.name}
          </Link>
        ))}
      </div>

      <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
        {list.map((a, i) => (
          <AdCard key={a.id} ad={a} index={i} />
        ))}
      </div>
    </div>
  );
}